import React, { useCallback, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  Typography,
  IconButton,
  Paper,
  Divider,
  TextField,
  Alert,
  CircularProgress,
} from '@mui/material';
import { ContentCopy as ContentCopyIcon, CheckCircle as CheckCircleIcon, Security as SecurityIcon } from '@mui/icons-material';

interface UserCreatedSuccessDialogProps {
  open: boolean;
  onClose: () => void;
  userData: {
    id?: number;
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    username: string;
    role: number;
  } | null;
  onSetup2FA?: (userId: number) => Promise<{ qrCode: string; secret: string }>;
  onEnable2FA?: (userId: number, code: string) => Promise<void>;
}

const UserCreatedSuccessDialog: React.FC<UserCreatedSuccessDialogProps> = ({ 
  open, 
  onClose, 
  userData,
  onSetup2FA,
  onEnable2FA
}) => {
  const [show2FASetup, setShow2FASetup] = useState(false);
  const [qrCode, setQrCode] = useState<string | null>(null);
  const [tfaSecret, setTfaSecret] = useState<string | null>(null);
  const [verificationCode, setVerificationCode] = useState('');
  const [tfaError, setTfaError] = useState<string | null>(null);
  const [tfaLoading, setTfaLoading] = useState(false);
  const [tfaSetupLoading, setTfaSetupLoading] = useState(false);

  const handleCopy = useCallback(async (text: string, fieldName: string) => {
    try {
      await navigator.clipboard.writeText(text);
      // You could show a toast notification here
    } catch (err) {
      console.error(`Failed to copy ${fieldName}:`, err);
    }
  }, []);

  const handleSetup2FAClick = useCallback(async () => {
    if (!userData?.id || !onSetup2FA) return;
    
    setTfaError(null);
    setTfaSetupLoading(true);
    
    try {
      const response = await onSetup2FA(userData.id);
      setQrCode(response.qrCode);
      setTfaSecret(response.secret);
      setShow2FASetup(true);
    } catch (err) {
      setTfaError(err instanceof Error ? err.message : 'Failed to setup 2FA');
    } finally {
      setTfaSetupLoading(false);
    }
  }, [userData?.id, onSetup2FA]);

  const handleEnable2FA = useCallback(async () => {
    if (!verificationCode || !userData?.id || !onEnable2FA) {
      setTfaError('Please enter the verification code');
      return;
    }

    setTfaError(null);
    setTfaLoading(true);

    try {
      await onEnable2FA(userData.id, verificationCode);
      setShow2FASetup(false);
      setQrCode(null);
      setTfaSecret(null);
      setVerificationCode('');
      setTfaError(null);
      // Close the success dialog after 2FA is enabled
      onClose();
    } catch (err) {
      setTfaError(err instanceof Error ? err.message : 'Failed to enable 2FA');
    } finally {
      setTfaLoading(false);
    }
  }, [verificationCode, userData?.id, onEnable2FA, onClose]);

  const handleSkip2FA = useCallback(() => {
    onClose();
  }, [onClose]);

  if (!userData) return null;

  const roleName = userData.role === 0 ? 'Root' : userData.role === 1 ? 'Editor' : 'Viewer';

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 2,
        }
      }}
    >
      <DialogTitle sx={{ pb: 2, fontWeight: 600, fontSize: '1.5rem', display: 'flex', alignItems: 'center', gap: 1 }}>
        {show2FASetup ? (
          <>
            <SecurityIcon sx={{ color: 'primary.main', fontSize: '2rem' }} />
            Setup 2FA for {userData.firstName} {userData.lastName}
          </>
        ) : (
          <>
            <CheckCircleIcon sx={{ color: 'success.main', fontSize: '2rem' }} />
            User Created Successfully
          </>
        )}
      </DialogTitle>
      <DialogContent>
        {show2FASetup ? (
          <>
            {tfaError && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {tfaError}
              </Alert>
            )}

            {qrCode && (
              <Box sx={{ textAlign: 'center', mb: 2 }}>
                <Typography variant="body2" gutterBottom>
                  Scan this QR code with your authenticator app:
                </Typography>
                <img src={qrCode} alt="2FA QR Code" style={{ maxWidth: '100%', marginTop: '16px' }} />
                {tfaSecret && (
                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                    Manual entry key: {tfaSecret}
                  </Typography>
                )}
              </Box>
            )}

            <TextField
              fullWidth
              label="Verification Code"
              value={verificationCode}
              onChange={(e) => setVerificationCode(e.target.value)}
              placeholder="Enter 6-digit code from authenticator"
              sx={{ mt: 2 }}
              disabled={tfaLoading}
            />
          </>
        ) : (
          <>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              Please save these credentials. They will not be shown again.
            </Typography>

        <Paper
          elevation={0}
          sx={{
            p: 2,
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            backgroundColor: '#f8fafc'
          }}
        >
          {/* Name */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem', fontWeight: 600 }}>
              Name
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace', mt: 0.5 }}>
              {userData.firstName} {userData.lastName}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Username */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem', fontWeight: 600 }}>
              Username
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace', mt: 0.5 }}>
              {userData.username}
            </Typography>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Email */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem', fontWeight: 600 }}>
              Email
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
              <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {userData.email}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleCopy(userData.email, 'Email')}
                sx={{
                  color: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#f0f7ff',
                  }
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Password */}
          <Box sx={{ mb: 2 }}>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem', fontWeight: 600 }}>
              Password
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 0.5 }}>
              <Typography variant="body1" sx={{ fontWeight: 500, fontFamily: 'monospace', wordBreak: 'break-all' }}>
                {userData.password}
              </Typography>
              <IconButton
                size="small"
                onClick={() => handleCopy(userData.password, 'Password')}
                sx={{
                  color: '#1976d2',
                  '&:hover': {
                    backgroundColor: '#f0f7ff',
                  }
                }}
              >
                <ContentCopyIcon fontSize="small" />
              </IconButton>
            </Box>
          </Box>

          <Divider sx={{ my: 2 }} />

          {/* Role */}
          <Box>
            <Typography variant="caption" color="text.secondary" sx={{ textTransform: 'uppercase', fontSize: '0.7rem', fontWeight: 600 }}>
              Role
            </Typography>
            <Typography variant="body1" sx={{ fontWeight: 500, textTransform: 'capitalize', mt: 0.5 }}>
              {roleName}
            </Typography>
          </Box>
        </Paper>
          </>
        )}
      </DialogContent>
      <DialogActions sx={{ p: 3, pt: 2, display: 'flex', justifyContent: 'space-between' }}>
        {!show2FASetup && userData.id && onSetup2FA && (
          <Button
            onClick={handleSetup2FAClick}
            variant="outlined"
            startIcon={<SecurityIcon />}
            disabled={tfaSetupLoading}
            sx={{
              textTransform: 'none',
              fontWeight: 500,
              borderRadius: 1.5,
              px: 3,
            }}
          >
            {tfaSetupLoading ? <CircularProgress size={20} /> : 'Setup 2FA'}
          </Button>
        )}
        <Box sx={{ display: 'flex', gap: 2, ml: 'auto' }}>
          {show2FASetup ? (
            <>
              <Button
                onClick={() => {
                  setShow2FASetup(false);
                  setQrCode(null);
                  setTfaSecret(null);
                  setVerificationCode('');
                  setTfaError(null);
                }}
                disabled={tfaLoading}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: 1.5,
                }}
              >
                Back
              </Button>
              <Button
                onClick={handleEnable2FA}
                variant="contained"
                disabled={!verificationCode || tfaLoading}
                sx={{
                  textTransform: 'none',
                  fontWeight: 500,
                  borderRadius: 1.5,
                  px: 3,
                  boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)',
                  '&:hover': {
                    boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                  }
                }}
              >
                {tfaLoading ? <CircularProgress size={20} /> : 'Enable 2FA'}
              </Button>
            </>
          ) : (
            <Button
              onClick={handleSkip2FA}
              variant="contained"
              sx={{
                textTransform: 'none',
                fontWeight: 500,
                borderRadius: 1.5,
                px: 3,
                boxShadow: '0 2px 8px rgba(25, 118, 210, 0.2)',
                '&:hover': {
                  boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                }
              }}
            >
              {userData.id && onSetup2FA ? 'Skip' : 'Close'}
            </Button>
          )}
        </Box>
      </DialogActions>
    </Dialog>
  );
};

export default UserCreatedSuccessDialog;

